"use client";

import { logError } from "@jetblue/dotcom-fe-logger";

const CONSENT_CHANGE_MARKER_KEY = "dotcom-consent-change-marker";

type ConsentMarkerRead =
  | {
      marker: null | string;
      succeeded: true;
    }
  | {
      succeeded: false;
    };

let consentSynchronizationRegistered = false;
let documentConsentChangeMarker: null | string = null;
let reloadRequested = false;
let storageErrorReported = false;

const reportStorageError = (error: unknown) => {
  if (storageErrorReported) {
    return;
  }

  storageErrorReported = true;

  void logError({
    error,
    message: "Consent synchronization storage is unavailable",
  });
};

const readConsentChangeMarker = (): ConsentMarkerRead => {
  try {
    return {
      marker: globalThis.localStorage.getItem(CONSENT_CHANGE_MARKER_KEY),
      succeeded: true,
    };
  } catch (error) {
    reportStorageError(error);
    return { succeeded: false };
  }
};

const reloadCurrentDocument = () => {
  if (reloadRequested) {
    return;
  }

  reloadRequested = true;
  globalThis.location.reload();
};

const reloadIfConsentMarkerChanged = () => {
  const markerRead = readConsentChangeMarker();

  if (
    !markerRead.succeeded ||
    markerRead.marker !== documentConsentChangeMarker
  ) {
    reloadCurrentDocument();
  }
};

const handleConsentMarkerStorage = (event: StorageEvent) => {
  if (event.key === CONSENT_CHANGE_MARKER_KEY) {
    reloadIfConsentMarkerChanged();
  }
};

const handleConsentPageShow = (event: PageTransitionEvent) => {
  if (event.persisted) {
    reloadIfConsentMarkerChanged();
  }
};

export const registerConsentSynchronization = () => {
  if (
    consentSynchronizationRegistered ||
    typeof globalThis.addEventListener !== "function"
  ) {
    return;
  }

  consentSynchronizationRegistered = true;

  const markerRead = readConsentChangeMarker();
  documentConsentChangeMarker = markerRead.succeeded ? markerRead.marker : null;

  globalThis.addEventListener("storage", handleConsentMarkerStorage);
  globalThis.addEventListener("pageshow", handleConsentPageShow);
};
export const invalidateConsentDocuments = () => {
  try {
    const nextMarker = new Date().toLocaleString();

    documentConsentChangeMarker = nextMarker;
    globalThis.localStorage.setItem(CONSENT_CHANGE_MARKER_KEY, nextMarker);
  } catch (error) {
    reportStorageError(error);
  }

  reloadCurrentDocument();
};
